import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Wrench, Euro, MapPin, Calendar, Phone, User as UserIcon,
  X, Check, Loader2, ExternalLink, Pencil, ChevronRight,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * AdminMaintenance — dedicated list of every booking that includes the
 * "Manutenção" extra. Admin can quote (set) the maintenance value inline,
 * open full booking details, or jump to the client's profile.
 */
export default function AdminMaintenance() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all · quoted · pending
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/maintenance");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    all: items.length,
    pending: items.filter(i => i.maintenance_value == null).length,
    quoted:  items.filter(i => i.maintenance_value != null).length,
  }), [items]);

  const visible = useMemo(() => {
    if (filter === "pending") return items.filter(i => i.maintenance_value == null);
    if (filter === "quoted")  return items.filter(i => i.maintenance_value != null);
    return items;
  }, [items, filter]);

  const totalInvoiced = useMemo(() => (
    items.reduce((s, i) => s + (Number(i.maintenance_value) || 0), 0)
  ), [items]);

  return (
    <div className="space-y-8" data-testid="admin-maintenance-page">
      <div>
        <div className="eyebrow mb-3 flex items-center gap-2">
          <Wrench size={12} /> Manutenções
        </div>
        <h1 className="font-display text-4xl sm:text-5xl">Trabalhos de manutenção</h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
          Lista de todas as reservas com o extra <strong>Manutenção</strong>. Defina o
          valor acordado com o cliente ou abra os detalhes completos para gerir cada
          trabalho.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard label="Total" value={counts.all} hint="trabalhos" />
        <SummaryCard label="Por orçamentar" value={counts.pending} accent="#f59e0b" hint="pendentes" />
        <SummaryCard label="Faturado" value={`${totalInvoiced.toFixed(2)} €`} accent="#d4af37" hint="soma dos valores" />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {[
          { k: "all", label: "Todas", accent: "#d4af37" },
          { k: "pending", label: "Por orçamentar", accent: "#f59e0b" },
          { k: "quoted",  label: "Orçamentadas",   accent: "#10b981" },
        ].map((t) => {
          const active = filter === t.k;
          return (
            <button key={t.k}
                    onClick={() => setFilter(t.k)}
                    className="px-4 py-2 text-xs uppercase tracking-widest border rounded-sm flex items-center gap-2"
                    style={{
                      borderColor: active ? t.accent : "#1a4640",
                      background: active ? `${t.accent}14` : "transparent",
                      color: active ? t.accent : "#a0b2af",
                    }}
                    data-testid={`maintenance-tab-${t.k}`}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.accent }} />
              {t.label}
              <span className="text-[10px] font-mono opacity-80">{counts[t.k]}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="space-y-3">
        {loading && <div className="text-sm text-[#a0b2af]">A carregar…</div>}
        {!loading && visible.length === 0 && (
          <div className="lux-card p-10 text-center text-sm text-[#a0b2af]">
            Sem trabalhos de manutenção {filter !== "all" ? "nesta categoria" : ""}.
          </div>
        )}
        {visible.map((b) => (
          <MaintenanceRow key={b.id} b={b} onOpen={() => setDetail(b)} />
        ))}
      </div>

      {/* Detail modal */}
      {detail && (
        <MaintenanceDetailModal
          booking={detail}
          onClose={() => setDetail(null)}
          onSaved={() => { load(); setDetail(null); }}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, hint, accent = "#a0b2af" }) {
  return (
    <div className="lux-card p-5">
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className="font-display text-3xl mt-1" style={{ color: accent }}>{value}</div>
      {hint && <div className="text-xs text-[#a0b2af] mt-1">{hint}</div>}
    </div>
  );
}

function MaintenanceRow({ b, onOpen }) {
  const quoted = b.maintenance_value != null;
  const accent = quoted ? "#10b981" : "#f59e0b";
  return (
    <button onClick={onOpen}
            className="w-full text-left lux-card p-5 hover:ring-1 hover:ring-[#d4af37]/50 transition-all group"
            style={{ borderLeftWidth: 3, borderLeftColor: accent, borderLeftStyle: "solid" }}
            data-testid={`maintenance-row-${b.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] flex items-center gap-2 flex-wrap">
            <Wrench size={10} style={{ color: accent }} />
            <span style={{ color: accent }}>{quoted ? "Orçamentada" : "Por orçamentar"}</span>
            {b.payment_reference && (
              <span className="font-mono normal-case tracking-normal">· {b.payment_reference}</span>
            )}
          </div>
          <div className="mt-2 text-base text-[#fdfbf7] flex items-center gap-2 flex-wrap">
            <Calendar size={14} className="text-[#d4af37]" />
            <span>{b.scheduled_date} · {b.scheduled_time}</span>
          </div>
          <div className="mt-1 text-sm text-[#cde3df]">
            <span className="text-[#d4af37]">{b.apartment_name || b.apt_type}</span>
            {b.apartment_address && <span className="text-[#a0b2af] ml-2 text-xs">· {b.apartment_address}</span>}
          </div>
          <div className="mt-1.5 text-xs text-[#a0b2af] flex items-center gap-2 flex-wrap">
            <UserIcon size={11} /> {b.guest_name || "—"}
            {b.client_phone && <span>· <Phone size={11} className="inline" /> {b.client_phone}</span>}
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Valor</div>
            <div className="font-display text-2xl mt-0.5" style={{ color: accent }}>
              {quoted ? `${Number(b.maintenance_value).toFixed(2)} €` : "A combinar"}
            </div>
          </div>
          <ChevronRight size={18} className="text-[#d4af37]" />
        </div>
      </div>
    </button>
  );
}

function MaintenanceDetailModal({ booking, onClose, onSaved }) {
  const [value, setValue] = useState(
    booking.maintenance_value != null ? String(booking.maintenance_value) : ""
  );
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  async function save() {
    const v = parseFloat(value);
    if (Number.isNaN(v) || v < 0) {
      toast.error("Valor inválido");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/bookings/${booking.id}/extra-price`, {
        extra_id: "manutencao",
        price: v,
      });
      toast.success("Valor guardado");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true"
         data-testid="maintenance-detail-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-sm border border-[#1a4640] bg-[#041f1b] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1a4640]">
          <div>
            <div className="eyebrow text-[#d4af37]">Manutenção</div>
            <div className="font-display text-2xl mt-1">{booking.apartment_name || booking.apt_type}</div>
          </div>
          <button onClick={onClose}
                  className="w-9 h-9 rounded-sm border border-[#1a4640] text-[#a0b2af] hover:text-[#d4af37] hover:border-[#d4af37] flex items-center justify-center"
                  data-testid="maintenance-modal-close">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-6 space-y-5">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Data" value={`${booking.scheduled_date} · ${booking.scheduled_time}`} />
            <Field label="Referência" value={booking.payment_reference} mono />
            <Field label="Hóspede" value={booking.guest_name} />
            <Field label="Telefone" value={booking.guest_phone || booking.client_phone} mono />
            <Field label="Email cliente" value={booking.client_email} mono />
            <Field label="Profissional" value={booking.assigned_employee_name} hint={!booking.assigned_employee_name ? "Por atribuir" : null} />
          </div>
          {booking.apartment_address && (
            <Field label="Morada" value={booking.apartment_address} icon={<MapPin size={12} />} />
          )}
          {booking.entry_instructions && (
            <Field label="Instruções de entrada" value={booking.entry_instructions} />
          )}
          {booking.notes && (
            <Field label="Observações do cliente" value={booking.notes} />
          )}

          {/* Quote editor */}
          <div className="lux-card p-4 space-y-3" style={{ borderColor: "#d4af37" }}>
            <div className="eyebrow text-[#d4af37] flex items-center gap-2">
              <Pencil size={12} /> Valor acordado para este trabalho
            </div>
            <div className="flex items-center gap-2">
              <Euro size={16} className="text-[#d4af37]" />
              <input
                type="number"
                step="0.01"
                min="0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Ex: 45.00"
                className="flex-1 bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm px-3 py-2 text-lg font-display text-[#d4af37]"
                data-testid="maintenance-value-input"
              />
              <button onClick={save}
                      disabled={saving}
                      className="btn-gold px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50 flex items-center gap-2"
                      data-testid="maintenance-save">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                Guardar
              </button>
            </div>
            <div className="text-[11px] text-[#a0b2af]">
              Este valor substitui o preço de catálogo (atualmente "a combinar") e é
              adicionado ao total da reserva. Pode ser alterado mais tarde.
            </div>
            <textarea value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Notas internas (opcional)"
                      rows={2}
                      className="w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm px-3 py-2 text-sm" />
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Link to={`/admin/reservas`}
                  className="text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] inline-flex items-center gap-1.5"
                  data-testid="maintenance-open-booking">
              <ExternalLink size={12} /> Abrir reserva completa
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, sub, mono, icon, hint }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className={`mt-1 ${mono ? "font-mono" : ""} ${hint ? "text-[#a0b2af] italic" : "text-[#fdfbf7]"}`}>
        {value || hint || "—"}
      </div>
      {sub && <div className="text-xs text-[#a0b2af] mt-0.5">{sub}</div>}
    </div>
  );
}
